import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// The sideways row every Watch rail shares: the college pages' Watch section
// (CollegeVideos.jsx, #4608) and the /videos page's per-college sections
// (VideosPage.jsx, #4728) both render their cards through this one row, never
// their own copy of its classes.
//
// `[contain:layout]` (#4854): on a phone, Pomona's college page measured 567
// CSS px wide on a 390 px viewport, and Michigan's (6 cards) measured 1002.
// The row's OWN box was always correctly held to its parent's width
// (clientWidth 350, matching `min-w-0` + `overflow-x-auto`) and really did
// scroll sideways inside itself (scrollLeft moved normally) - but Chromium
// still counted the row's un-clipped flex-item content (a `shrink-0` card
// per video, none of them wrapping) toward `document.documentElement
// .scrollWidth`, so the whole page measured as wide as the row's content and
// a phone zoomed the page out to fit. Confirmed live (Pomona, production,
// 2026-10-19): hiding the row alone dropped documentElement.scrollWidth from
// 566 to 382 (matching clientWidth); restoring it brought 566 back.
// `contain: layout` on the row (CSS containment: its internal layout cannot
// affect anything outside it) fixed exactly that, live, with no change to
// the row's own scrolling (scrollLeft still reaches the same position with
// or without it) - confirmed before writing this fix. `display: grid`
// (desktop, `lg:grid`) tests clean without it too, but the row is `flex`
// everywhere narrower than `lg`, which is where this shipped broken.
//
// More than ACROSS cards (#5707; founder 2026-10-28, R-99: "more than 5, show
// a right arrow to scroll right instead of putting in the next line"): on a
// wide screen the row stays one line and scrolls sideways, each card keeping
// the width it has five across (the row less its four 14px gaps, over five),
// with a right arrow at the row's edge and a left arrow once scrolled. A
// phone keeps the swipe it always had; the arrows are wide-screen only
// (`hidden lg:flex`, so a phone's tab order never meets them). Five or
// fewer: the five-across grid, exactly as before.
export const ACROSS = 5;

const RAIL_BASE = 'flex min-w-0 snap-x snap-mandatory gap-3.5 overflow-x-auto pb-2 [contain:layout]';
const RAIL_CLASS = `${RAIL_BASE} lg:grid lg:grid-cols-5 lg:overflow-visible`;
const RAIL_SCROLL_CLASS = `${RAIL_BASE} lg:[&>li]:w-[calc((100%_-_3.5rem)_/_5)]`;

const ARROW_CLASS = 'absolute top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full '
    + 'border border-[#E0DED8] bg-white text-[#1A4D2E] shadow-md hover:bg-[#F7FBF7] '
    + 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A4D2E] focus-visible:ring-offset-2 lg:flex';

const prefersReducedMotion = () => typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Where the row sits. An unmeasured row (scrollWidth 0: before layout, or the
// build's prerender) is "not at the end", so the right arrow the build drew
// stays and the browser's first render matches it.
const edgesOf = (el) => ({
    start: el.scrollLeft <= 1,
    end: el.scrollWidth > 0 && el.scrollLeft + el.clientWidth >= el.scrollWidth - 1,
});

/** `label`: whose videos these are, for the arrows' names ("More UCLA videos"). */
export default function VideoRail({ children, label }) {
    if (React.Children.toArray(children).length <= ACROSS) return <ul className={RAIL_CLASS}>{children}</ul>;
    return <ScrollingRail label={label}>{children}</ScrollingRail>;
}

function ScrollingRail({ children, label }) {
    const id = useId();
    const row = useRef(null);
    const prev = useRef(null);
    const next = useRef(null);
    const handoff = useRef(null);
    const [edges, setEdges] = useState({ start: true, end: false });

    // An arrow that disappears while it has focus (the right one at the end,
    // the left one back at the start) would drop focus to the page, so the
    // other arrow, showing by then, takes it. Read before the re-render,
    // while the focused arrow is still in the document.
    const measure = useCallback(() => {
        const el = row.current;
        if (!el) return;
        const now = edgesOf(el);
        const active = document.activeElement;
        if (now.end && active && active === next.current) handoff.current = prev;
        else if (now.start && active && active === prev.current) handoff.current = next;
        setEdges((was) => (was.start === now.start && was.end === now.end ? was : now));
    }, []);

    useEffect(() => {
        if (!handoff.current) return;
        handoff.current.current?.focus();
        handoff.current = null;
    }, [edges]);

    useEffect(() => {
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [measure]);

    // Re-measured whenever the cards change too: on /videos a theme chip can
    // add cards to a row already scrolled to its end, with no scroll event to
    // bring the right arrow back.
    const count = React.Children.count(children);
    useEffect(() => { measure(); }, [measure, count]);

    const step = (dir) => () => {
        const el = row.current;
        if (!el) return;
        el.scrollBy({ left: dir * el.clientWidth, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    };
    const who = label ? `${label} ` : '';

    // DOM order is visual order: left arrow, the row, right arrow.
    return (
        <div className="relative min-w-0">
            {!edges.start && (
                <button
                    ref={prev}
                    type="button"
                    aria-controls={id}
                    aria-label={`Previous ${who}videos`}
                    onClick={step(-1)}
                    data-testid="video-rail-prev"
                    className={`${ARROW_CLASS} left-2`}
                >
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
            )}
            <ul id={id} ref={row} onScroll={measure} data-testid="video-rail" className={RAIL_SCROLL_CLASS}>{children}</ul>
            {!edges.end && (
                <button
                    ref={next}
                    type="button"
                    aria-controls={id}
                    aria-label={`More ${who}videos`}
                    onClick={step(1)}
                    data-testid="video-rail-next"
                    className={`${ARROW_CLASS} right-2`}
                >
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
            )}
        </div>
    );
}
